import { create } from "zustand";
import axios from "axios";
import { getUnexpiredToken } from "@/utils/utils";

interface EnvVariablesState {
  // Data
  envVariables: Record<string, string>;
  isLoading: boolean;
  error: string | null;
  lastFetchedProjectId: string | null;

  // Actions
  fetchEnvVariables: (projectId: string, session: any) => Promise<void>;
  setEnvVariables: (vars: Record<string, string>) => void;
  updateEnvVariable: (key: string, value: string | null) => void;
  clearEnvVariables: () => void;
}

export const useEnvVariablesStore = create<EnvVariablesState>((set, get) => ({
  // Initial state
  envVariables: {},
  isLoading: false,
  error: null,
  lastFetchedProjectId: null,

  // Actions
  fetchEnvVariables: async (projectId: string, session: any) => {
    const state = get();

    // Skip if already loading for this project
    if (state.isLoading && state.lastFetchedProjectId === projectId) {
      return;
    }

    set({ isLoading: true, error: null, lastFetchedProjectId: projectId });

    try {
      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        throw new Error("Failed to get valid authentication token");
      }

      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${projectId}/envVariables`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      set({
        envVariables: response.data || {},
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      console.error("Failed to fetch environment variables:", error);
      set({
        isLoading: false,
        error: error.message || "Failed to fetch environment variables",
        envVariables: {},
      });
    }
  },

  setEnvVariables: (vars: Record<string, string>) => {
    set({ envVariables: vars });
  },

  updateEnvVariable: (key: string, value: string | null) => {
    set((state) => {
      const newVars = { ...state.envVariables };
      if (value === null) {
        delete newVars[key];
      } else {
        newVars[key] = value;
      }
      return { envVariables: newVars };
    });
  },

  clearEnvVariables: () => {
    set({ envVariables: {}, lastFetchedProjectId: null });
  },
}));
